import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleCheckout = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(
        'https://dormteymhczmditmmfkm.supabase.co/functions/v1/create-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quantity,
            successUrl: `${window.location.origin}/success?success=true`,
            cancelUrl: `${window.location.origin}/success?canceled=true`,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Checkout response error:', errorData);
        alert(`Checkout failed: ${errorData.error || 'Unknown error'}`);
        setIsProcessing(false);
        return;
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        alert('Unable to process checkout. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(
        `An error occurred: ${
          error instanceof Error ? error.message : 'Please try again'
        }`
      );
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-white">Southern Roots</h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-sm font-medium text-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('product')}
                className="text-sm font-medium text-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Product
              </button>
              <Link
                to="/blog"
                className="text-sm font-medium text-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Recipes
              </Link>
              <button
                onClick={() => scrollToSection('story')}
                className="text-sm font-medium text-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Our Story
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-medium text-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-800 cursor-pointer"
            >
              <i
                className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl text-white`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-gray-800">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('hero')}
                className="block w-full text-left text-sm font-medium text-white hover:text-red-600 py-2 cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('product')}
                className="block w-full text-left text-sm font-medium text-white hover:text-red-600 py-2 cursor-pointer"
              >
                Product
              </button>
              <Link
                to="/blog"
                className="block w-full text-left text-sm font-medium text-white hover:text-red-600 py-2 cursor-pointer"
              >
                Recipes
              </Link>
              <button
                onClick={() => scrollToSection('story')}
                className="block w-full text-left text-sm font-medium text-white hover:text-red-600 py-2 cursor-pointer"
              >
                Our Story
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-sm font-medium text-white hover:text-red-600 py-2 cursor-pointer"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audrey%20Nicole.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=h:1500,cg:true"
            alt="Southern Roots Bloody Mary"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Southern Roots<br />
            Bloody Mary Mix
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto font-light">
            Mixing Good Times with a Good Cause
          </p>
          <p className="text-lg md:text-xl text-white/90 mb-12 max-w-2xl mx-auto">
            Crafted with authentic Southern tradition and the finest ingredients for the perfect Bloody Mary experience
          </p>
          <button
            onClick={() => scrollToSection('product')}
            className="inline-block bg-red-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Buy Now
          </button>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Premium Bloody Mary Mix
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our signature Bloody Mary mix is inspired by timeless Southern recipes, perfected over generations. We use only the freshest, locally sourced ingredients to create a rich and flavorful experience that embodies the warmth and hospitality of the South.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                From the first sip to the last, you'll taste the passion and care we pour into every bottle. Perfect for brunches, gatherings, or any time you want to enjoy a classic cocktail with authentic Southern flavor.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Price</p>
                    <p className="text-3xl font-bold text-gray-900">$12.99</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center hover:border-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line text-lg"></i>
                    </button>
                    <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center hover:border-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-lg"></i>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={isProcessing}
                  className="w-full bg-red-600 text-white py-4 rounded-full text-lg font-semibold hover:bg-red-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing ? 'Processing...' : `Add to Cart - $${(12.99 * quantity).toFixed(2)}`}
                </button>
              </div>

              <div className="flex items-start space-x-3 text-sm text-gray-600">
                <i className="ri-shield-check-line text-xl text-green-600 mt-1"></i>
                <p>Every purchase supports local communities and charitable causes across the South</p>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audre-392e910.jpg/:/cr=t:5.54%25,l:0%25,w:100%25,h:88.92%25/rs=w:600,h:800,cg:true"
                  alt="Southern Roots Bloody Mary Mix Bottle"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Ingredients Section */}
      <section id="ingredients" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Premium Ingredients</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We believe the best cocktails start with the finest ingredients. Every bottle is crafted with care using locally sourced, premium components.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-plant-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fresh Tomatoes</h3>
              <p className="text-gray-600 leading-relaxed">
                Vine-ripened tomatoes sourced from local Southern farms, providing a rich, authentic base for our signature mix.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-fire-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Bold Spices</h3>
              <p className="text-gray-600 leading-relaxed">
                A carefully balanced blend of horseradish, hot sauce, Worcestershire, and secret Southern spices for the perfect kick.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Natural Ingredients</h3>
              <p className="text-gray-600 leading-relaxed">
                No artificial preservatives or flavors. Just pure, natural ingredients that deliver authentic Southern taste in every sip.
              </p>
            </div>
          </div>

          <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What Makes It Special</h3>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-1"></i>
                    <span className="text-gray-700">Locally sourced, farm-fresh ingredients</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-1"></i>
                    <span className="text-gray-700">Traditional Southern recipe passed down through generations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-1"></i>
                    <span className="text-gray-700">Perfect balance of savory, spicy, and tangy flavors</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-1"></i>
                    <span className="text-gray-700">No artificial preservatives or additives</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-1"></i>
                    <span className="text-gray-700">Supports local communities with every purchase</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/blob.png/:/cr=t:13.08%25,l:0%25,w:86.92%25,h:86.92%25/rs=w:600,cg:true,m"
                  alt="Ingredients"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sip & Savor Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Sip & Savor</h2>
            <p className="text-lg text-gray-600">Experience the perfect Bloody Mary moment</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audre-59f1f26.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:600,cg:true"
                alt="Bloody Mary"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/blob.png/:/cr=t:13.08%25,l:0%25,w:86.92%25,h:86.92%25/rs=w:600,cg:true,m"
                alt="Ingredients"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audre-59523cb.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:600,cg:true"
                alt="Cocktail"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audre-d9e82a9.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:600,cg:true"
                alt="Garnished Drink"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from those who love our Bloody Mary mix
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6 italic">
                "Thank you so much, Southern Roots, for the awesome Bloody Mary mix! It was absolutely delicious and a huge hit at my family gatherings. The flavor has just the right amount of spice—bold and flavorful without being too hot. Shipping was quick and arrived at my house in just a few days, which was a great bonus. I'll definitely be ordering more in the future. Thanks again for a great product!"
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold text-lg">JB</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">J.C Barrett</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6 italic">
                "As a Bloody Mary aficionado, I've always been a purist and made my own from scratch. I've never found a mix I didn't end up seasoning and tweaking myself until I tried Southern Roots Bloody Mary Mix. As a savory girl, this mix definitely hits the spot and is my new go to. I come from a family of Bloody Mary fans, and we are all hooked on Southern Roots!"
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold text-lg">SK</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Sarah Keen</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
                <i className="ri-star-fill text-2xl text-yellow-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6 italic">
                "Southern Roots is so yummy and delicious! Honestly, it's one of the best Bloody Mary mixes I've ever had. The flavor is so good — not too spicy, not too bland — just really well balanced. You can tell it's made with care. I've had it on its own and dressed up, and it never disappoints. Definitely one I'll keep buying."
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold text-lg">CS</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Courtney Spivey</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => scrollToSection('product')}
              className="inline-block bg-red-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Try It Yourself
            </button>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section id="story" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://img1.wsimg.com/isteam/ip/ceeb0eee-854c-4a57-a4cf-ca3ce24c0064/Southern%20Roots%20Bloody%20Mary%20Mix%20-%20Audrey%20Nicole.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:800,cg:true"
                alt="Southern Roots Story"
                className="w-full h-auto"
              />
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">A Taste of Tradition</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  At Southern Roots, we believe that the best moments are those shared with friends and family, especially when accompanied by the perfect Bloody Mary. But what if those moments could also help make the world a better place? That's where we come in.
                </p>
                <p>
                  Our Bloody Mary mix is inspired by timeless Southern recipes, perfected over generations. We use only the freshest, locally sourced ingredients to create a rich and flavorful experience that embodies the warmth and hospitality of the South.
                </p>
                <p>
                  From the first sip to the last, you'll taste the passion and care we pour into every bottle. But what truly sets us apart is our commitment to giving back to the communities that inspire us.
                </p>
                <p className="font-semibold text-red-600">
                  With every purchase, you're not just enjoying a premium cocktail mix – you're supporting local charities and making a positive impact across the South.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-600">Questions about our products? We'd love to hear from you!</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="bg-gray-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Southern Roots</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <i className="ri-map-pin-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900">Address</p>
                      <p className="text-gray-600">
                        2170 Highland Ave South Ste 200<br />
                        Birmingham, AL 35205
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <i className="ri-time-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900">Hours</p>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 5:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Better yet, see us in person!</h3>
                <p className="mb-6">
                  Do you have questions or comments about our products? We love to hear from our customers. Send us a message, and we will get back to you soon.
                </p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/114092685085564"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-2xl"></i>
                  </a>
                  <a
                    href="https://www.instagram.com/southernflavorr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-fill text-2xl"></i>
                  </a>
                  <a
                    href="https://www.tiktok.com/@Southern.Roots29"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-tiktok-fill text-2xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Message</h3>
              <form id="contact-form" data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Your message... (max 500 characters)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-red-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-amber-50 to-orange-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Copyright © 2024 Southern Roots - All Rights Reserved.</p>
            <div className="flex justify-center space-x-6 text-sm text-gray-600">
              <a href="#" className="hover:text-red-600 transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <span>•</span>
              <a href="#" className="hover:text-red-600 transition-colors cursor-pointer">
                Terms &amp; Conditions
              </a>
              <span>•</span>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-red-600 transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}